import { useMemo } from 'react'
import { UserDTO } from '../models/User'
import { LoggedUser } from '../models/LoggedUser'
import * as roleUtils from '../../../common/utils/roleUtils'
import * as permissions from '../../../common/permission'

export const useUserRoles = (user: UserDTO | LoggedUser | undefined | null) => {
  return useMemo(() => {
    if (!user) {
      return {
        hasRole: () => false,
        hasAnyRole: () => false,
        hasAllRoles: () => false,
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,

        isPatient: false,
        isFamiliar: false,
        isMed: false,
        isAdmin: false,
        userType: null,

        canEditOwnData: false,
        canChangeOwnPassword: false,
        canCreateUser: false,
        canCreateFamiliar: false,
        canViewAnyUser: false,
        canEditAnyUser: false,
        canDeleteUser: false,
        canManageRoles: false,

        canAccessHome: false,
        canViewPatients: false,
        canAccessCalendar: false,
        canAccessCalendarMenu: false,
        canViewStatistics: false,
        canAccessStatisticsMenu: false,
        canManageMedicines: false,
        canAccessForms: false,
        canAccessResources: false,
        canAccessNews: false,
        canAccessArticles: false,
        canAccessMessenger: false,

        canAccessTraining: false,
        canAccessTrainingMenu: false,
        canAccessTrainingCatalog: false,
        canCreateTraining: false,
        canEditTraining: false,
        canDeleteTraining: false,

        canViewUserTraining: false,
        canCreateUserTraining: false,
        canEditUserTraining: false,
        canDeleteUserTraining: false,
        canCompleteUserTraining: false,

        canCreatePatient: false,
        canEditPatientData: false,
        canManagePatientCircle: false,
        canViewPatientDetails: false,
        canViewPatientStats: false,
        canViewPatientCalendar: false,
        canEditPatientCalendar: false,

        canCreateTreatment: false,
        canViewTreatment: false,
        canEditTreatment: false,
        canDeleteTreatment: false,

        canEditPatients: false,
        canCreateForms: false,
        canAnswerForms: false,

        canLinkWearable: false,
        canViewWearable: false,

        canUploadFiles: false,
        canViewFiles: false,
        canDeleteFiles: false,
        canShareFiles: false,

        canSendMessages: false,
        canViewMessages: false,

        canManageContent: false,
        canCreateContent: false,
        canEditContent: false,
        canDeleteContent: false,
        canViewContent: false,

        canViewCalendar: false,
        canEditCalendar: false,
        canViewAnyCalendar: false,
        canEditAnyCalendar: false,

        canViewOwnStats: false,
        canViewAnyStats: false,
      }
    }

    return {
      hasRole: (role: string) => roleUtils.hasRole(user, role),
      hasAnyRole: (roles: string[]) => roleUtils.hasAnyRole(user, roles),
      hasAllRoles: (roles: string[]) => roleUtils.hasAllRoles(user, roles),
      hasPermission: (permission: string) => permissions.hasPermission(user, permission),
      hasAnyPermission: (permissionsList: string[]) =>
        permissions.hasAnyPermission(user, permissionsList),
      hasAllPermissions: (permissionsList: string[]) =>
        permissions.hasAllPermissions(user, permissionsList),

      isPatient: roleUtils.isPatient(user),
      isFamiliar: roleUtils.isFamiliar(user),
      isMed: roleUtils.isMed(user),
      isAdmin: roleUtils.isAdmin(user),
      userType: roleUtils.getUserType(user),

      canEditOwnData: permissions.canEditOwnData(user),
      canChangeOwnPassword: permissions.canChangeOwnPassword(user),
      canCreateUser: permissions.canCreateUser(user),
      canCreateFamiliar: permissions.canCreateFamiliar(user),
      canViewAnyUser: permissions.canViewAnyUser(user),
      canEditAnyUser: permissions.canEditAnyUser(user),
      canDeleteUser: permissions.canDeleteUser(user),
      canManageRoles: permissions.canManageRoles(user),

      canAccessHome: permissions.canAccessHome(user),
      canViewPatients: permissions.canViewPatients(user),
      canAccessCalendar: permissions.canAccessCalendar(user),
      canAccessCalendarMenu: permissions.canAccessCalendarMenu(user),
      canViewStatistics: permissions.canViewStatistics(user),
      canAccessStatisticsMenu: permissions.canAccessStatisticsMenu(user),
      canManageMedicines: permissions.canManageMedicines(user),
      canAccessForms: permissions.canAccessForms(user),
      canAccessResources: permissions.canAccessResources(user),
      canAccessNews: permissions.canAccessNews(user),
      canAccessArticles: permissions.canAccessArticles(user),
      canAccessMessenger: permissions.canAccessMessenger(user),

      canAccessTraining: permissions.canAccessTraining(user),
      canAccessTrainingMenu: permissions.canAccessTrainingMenu(user),
      canAccessTrainingCatalog: permissions.canAccessTrainingCatalog(user),
      canCreateTraining: permissions.canCreateTraining(user),
      canEditTraining: permissions.canEditTraining(user),
      canDeleteTraining: permissions.canDeleteTraining(user),

      canViewUserTraining: permissions.canViewUserTraining(user),
      canCreateUserTraining: permissions.canCreateUserTraining(user),
      canEditUserTraining: permissions.canEditUserTraining(user),
      canDeleteUserTraining: permissions.canDeleteUserTraining(user),
      canCompleteUserTraining: permissions.canCompleteUserTraining(user),

      canCreatePatient: permissions.canCreatePatient(user),
      canEditPatientData: permissions.canEditPatientData(user),
      canManagePatientCircle: permissions.canManagePatientCircle(user),
      canViewPatientDetails: permissions.canViewPatientDetails(user),
      canViewPatientStats: permissions.canViewPatientStats(user),
      canViewPatientCalendar: permissions.canViewPatientCalendar(user),
      canEditPatientCalendar: permissions.canEditPatientCalendar(user),

      canCreateTreatment: permissions.canCreateTreatment(user),
      canViewTreatment: permissions.canViewTreatment(user),
      canEditTreatment: permissions.canEditTreatment(user),
      canDeleteTreatment: permissions.canDeleteTreatment(user),

      canEditPatients: permissions.canEditPatientData(user),
      canCreateForms: permissions.canCreateForms(user),
      canAnswerForms: permissions.canAnswerForms(user),

      canLinkWearable: permissions.canLinkWearable(user),
      canViewWearable: permissions.canViewWearable(user),

      canUploadFiles: permissions.canUploadFiles(user),
      canViewFiles: permissions.canViewFiles(user),
      canDeleteFiles: permissions.canDeleteFiles(user),
      canShareFiles: permissions.canShareFiles(user),

      canSendMessages: permissions.canSendMessages(user),
      canViewMessages: permissions.canViewMessages(user),

      canManageContent: permissions.canManageContent(user),
      canCreateContent: permissions.canCreateContent(user),
      canEditContent: permissions.canEditContent(user),
      canDeleteContent: permissions.canDeleteContent(user),
      canViewContent: permissions.canViewContent(user),

      canViewCalendar: permissions.canViewCalendar(user),
      canEditCalendar: permissions.canEditCalendar(user),
      canViewAnyCalendar: permissions.canViewAnyCalendar(user),
      canEditAnyCalendar: permissions.canEditAnyCalendar(user),

      canViewOwnStats: permissions.canViewOwnStats(user),
      canViewAnyStats: permissions.canViewAnyStats(user),
    }
  }, [user])
}
